import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWorkspaceData } from "@/hooks/use-workspace-data";
/**
 * Project toolbar — search bar, view toggle, new-project / new-folder buttons.
 */
import { LayoutGrid, List, Plus, Search } from "lucide-react";

export interface WorkspaceProjectToolbarProps {
  view: "gallery" | "list";
  onViewChange: (v: "gallery" | "list") => void;
  query: string;
  onQueryChange: (q: string) => void;
  onCreateProject: () => void;
  onCreateFolder: () => void;
}

export function WorkspaceProjectToolbar({
  view,
  onViewChange,
  query,
  onQueryChange,
  onCreateProject,
  onCreateFolder,
}: WorkspaceProjectToolbarProps): JSX.Element {
  const flags = useWorkspaceData((s) => s.flags);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-1 items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search projects and folders…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            className="pl-8"
            aria-label="Search workspace"
          />
        </div>
        <div className="flex items-center rounded-md border border-border bg-card p-0.5">
          <Button
            variant={view === "gallery" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => onViewChange("gallery")}
            aria-pressed={view === "gallery"}
            aria-label="Gallery view"
            className="h-7 px-2"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={view === "list" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => onViewChange("list")}
            aria-pressed={view === "list"}
            aria-label="List view"
            className="h-7 px-2"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={onCreateFolder} variant="outline" disabled={!flags.folders_writable}>
          <Plus className="mr-1 h-4 w-4" />
          New folder
        </Button>
        <Button onClick={onCreateProject} disabled={!flags.projects_writable}>
          <Plus className="mr-1 h-4 w-4" />
          New project
        </Button>
      </div>
    </div>
  );
}
